import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../utils/api';

// Use apiService for structured API calls, axios for direct HTTP calls
const api = apiService;

const BirthRecordForm = ({ mode = 'create', recordId = null }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditMode = mode === 'edit' && recordId;
  const isAdmin = user?.role === 'admin';
  
  // For non-admin users, initialize with their clinic data immediately
  const initialFormData = {
    registration_date: '',
    registration_id: '',
    organization_id: (!isAdmin && user?.organization_id) ? user.organization_id : '',
    district_id: '',
    township_id: '',
    clinic_id: (!isAdmin && user?.clinic_code) ? user.clinic_code : '',
    baby_name: '',
    baby_gender_id: '',
    baby_date_of_birth: '',
    baby_week_of_day: '',
    baby_birth_time_hour: '',
    baby_birth_time_minute: '',
    baby_birth_time_ampm: 'AM',
    baby_birth_place_choose: '',
    baby_birth_place_district: '',
    baby_birth_place_township: '',
    baby_birth_place_village: '',
    baby_birth_type: 'single',
    baby_twin_order: '',
    baby_birth_order: '',
    // Add other location fields for baby birth place
    baby_birth_place_other_district: '',
    baby_birth_place_other_township: '',
    baby_birth_place_other_village: '',
    father_name: '',
    father_national_id: '',
    father_myanmar_nrc_area_code: '',
    father_myanmar_nrc_township_code: '',
    father_myanmar_nrc_type: '',
    father_myanmar_nrc_number: '',
    father_ethnicity_id: '',
    father_ethnicity_other_specify: '',
    father_religion_id: '',
    father_address: '',
    mother_name: '',
    mother_national_id: '',
    mother_myanmar_nrc_area_code: '',
    mother_myanmar_nrc_township_code: '',
    mother_myanmar_nrc_type: '',
    mother_myanmar_nrc_number: '',
    mother_ethnicity_id: '',
    mother_ethnicity_other_specify: '',
    mother_religion_id: '',
    mother_address: '',
    delivered_by_name: '',
    delivered_by_position_id: '',
    delivered_by_address: '',
    delivered_by_signature: false,
    recorder_name: '',
    recorder_position: '',
    recorder_clinic_id: '',
    recorder_clinic_name: (!isAdmin && user?.clinic_name) ? user.clinic_name : '',
    recorder_signature: false,
    // Birth record fields
    delivery_type_id: '',
    birth_record_baby_weight: '',
    birth_record_baby_height: '',
    birth_record_baby_head_circumference: '',
    birth_record_baby_birth_defected: false,
    birth_defect_description: '',
    mother_right_thumb_print: false,
    father_right_thumb_print: false,
    baby_right_foot_print: false
  };
  
  const [formData, setFormData] = useState(initialFormData);

  const [referenceData, setReferenceData] = useState({
    organizations: [],
    districts: [],
    townships: [],
    clinics: [],
    villages: [],
    // Separate arrays for birth place location
    birthPlaceDistricts: [],
    birthPlaceTownships: [],
    birthPlaceVillages: [],
    genders: [],
    ethnicities: [],
    religions: [],
    birthPlaces: [],
    nrcAreas: [],
    fatherNrcTownships: [],
    motherNrcTownships: [],
    deliveryTypes: [],
    positions: []
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState(null);
  const [checkingExisting, setCheckingExisting] = useState(true);
  const [fatherEthnicityIsOther, setFatherEthnicityIsOther] = useState(false);
  const [motherEthnicityIsOther, setMotherEthnicityIsOther] = useState(false);
  const [fatherHasNRC, setFatherHasNRC] = useState(false);
  const [motherHasNRC, setMotherHasNRC] = useState(false);
  const [shouldRedirectAfterSubmit, setShouldRedirectAfterSubmit] = useState(true);
  
  // Single page form - no step navigation needed

  // Test API connectivity
  const testAPIConnection = useCallback(async () => {
    try {

      const response = await api.utility.testDatabase();

      return true;
    } catch (error) {
      console.error('❌ API connection test failed:', error);
      return false;
    }
  }, []);

  // Load reference data on component mount
  const loadReferenceData = useCallback(async () => {
    try {
      setCheckingExisting(true);
      setErrors({});


      // Test API connection first
      const connectionOk = await testAPIConnection();
      if (!connectionOk) {
        throw new Error('Cannot connect to backend server. Please ensure the server is running on http://localhost:8000');
      }

      // Load districts first

      const districts = await api.referenceData.getDistricts();


      // Load other reference data

      const [
        organizations,
        genders,
        ethnicities,
        religions,
        birthPlaces,
        deliveryTypes,
        positions,
        allClinics,
        nrcAreas
      ] = await Promise.all([
        api.referenceData.getOrganizations().catch(err => {
          console.warn('Organizations failed to load:', err.message);
          return [];
        }),
        api.referenceData.getGender().catch(err => {
          console.warn('Gender failed to load:', err.message);
          return [];
        }),
        api.referenceData.getEthnicities().catch(err => {
          console.warn('Ethnicities failed to load:', err.message);
          return [];
        }),
        api.referenceData.getReligions().catch(err => {
          console.warn('Religions failed to load:', err.message);
          return [];
        }),
        api.referenceData.getBirthPlaces().catch(err => {
          console.warn('Birth places failed to load:', err.message);
          return [];
        }),
        api.referenceData.getDeliveryTypes().catch(err => {
          console.warn('Delivery types failed to load:', err.message);
          return [];
        }),
        api.referenceData.getPositions().catch(err => {
          console.warn('Positions failed to load:', err.message);
          return [];
        }),
        api.referenceData.getAllClinics().catch(err => {
          console.warn('All clinics failed to load:', err.message);
          return [];
        }),
        apiService.axios.get('/nrc/areas').then(res => res.data).catch(err => {
          console.warn('NRC areas failed to load:', err.message);
          return [];
        })
      ]);

      setReferenceData({
        organizations: organizations || [],
        districts: districts || [],
        townships: [],
        clinics: allClinics || [], // Use all clinics for recorder section
        villages: [],
        // Initialize birth place arrays with same districts
        birthPlaceDistricts: districts || [],
        birthPlaceTownships: [],
        birthPlaceVillages: [],
        genders: genders || [],
        ethnicities: ethnicities || [],
        religions: religions || [],
        birthPlaces: birthPlaces || [],
        deliveryTypes: deliveryTypes || [],
        positions: positions || [],
        nrcAreas: nrcAreas || [],
        fatherNrcTownships: [],
        motherNrcTownships: []
      });


    } catch (error) {
      console.error('❌ Error loading reference data:', error);
      setErrors({ general: `Failed to load form data: ${error.message}` });
    } finally {
      setCheckingExisting(false);
    }
  }, [testAPIConnection]);

  // Load existing record data for edit mode
  const loadExistingRecord = useCallback(async () => {
    if (!isEditMode || !recordId) return;

    try {
      setCheckingExisting(true);
      setErrors({});


      const record = await api.birthRecords.getBirthRecord(recordId);


      // Map the record data to form fields
      const mappedData = {
        registration_date: record.registration_date || '',
        registration_id: record.registration_id || '',
        organization_id: record.organization_id || '',
        district_id: record.district_id || '',
        township_id: record.township_id || '',
        clinic_id: record.clinic_id || '',
        baby_name: record.baby_name || '',
        baby_gender_id: record.baby_gender_id || '',
        baby_date_of_birth: record.baby_date_of_birth || '',
        baby_week_of_day: record.baby_week_of_day || '',
        baby_birth_time_hour: record.baby_birth_time_hour ?? '',
        baby_birth_time_minute: record.baby_birth_time_minute ?? '',
        baby_birth_time_ampm: record.baby_birth_time_ampm || 'AM',
        baby_birth_place_choose: record.baby_birth_place_choose || '',
        baby_birth_place_district: record.baby_birth_place_district || '',
        baby_birth_place_township: record.baby_birth_place_township || '',
        baby_birth_place_village: record.baby_birth_place_village || '',
        baby_birth_type: record.baby_birth_type || 'single',
        baby_twin_order: record.baby_twin_order || '',
        baby_birth_order: record.baby_birth_order || '',
        // Add other location fields for baby birth place
        baby_birth_place_other_district: record.baby_birth_place_other_district || '',
        baby_birth_place_other_township: record.baby_birth_place_other_township || '',
        baby_birth_place_other_village: record.baby_birth_place_other_village || '',
        // Father information
        father_name: record.father_name || '',
        father_national_id: record.father_national_id || '',
        father_ethnicity_id: record.father_ethnicity_id || '',
        father_ethnicity_other_specify: record.father_ethnicity_other_specify || '',
        father_religion_id: record.father_religion_id || '',
        father_address: record.father_address || '',
        mother_name: record.mother_name || '',
        mother_national_id: record.mother_national_id || '',
        mother_ethnicity_id: record.mother_ethnicity_id || '',
        mother_ethnicity_other_specify: record.mother_ethnicity_other_specify || '',
        mother_religion_id: record.mother_religion_id || '',
        mother_address: record.mother_address || '',
        delivered_by_name: record.delivered_by_name || '',
        delivered_by_position_id: record.delivered_by_position_id || '',
        delivered_by_address: record.delivered_by_address || '',
        delivered_by_signature: record.delivered_by_signature || false,
        recorder_name: record.recorder_name || '',
        recorder_position: record.recorder_position || '',
        recorder_clinic_name: record.recorder_clinic_name || '',
        recorder_signature: record.recorder_signature || false,
        delivery_type_id: record.delivery_type_id || '',
        birth_record_baby_weight: record.birth_record_baby_weight || '',
        birth_record_baby_height: record.birth_record_baby_height || '',
        birth_record_baby_head_circumference: record.birth_record_baby_head_circumference || '',
        birth_record_baby_birth_defected: record.birth_record_baby_birth_defected || false,
        birth_defect_description: record.baby_birth_defect_description || '',
        mother_right_thumb_print: record.mother_right_thumb_print || false,
        father_right_thumb_print: record.father_right_thumb_print || false,
        baby_right_foot_print: record.baby_right_foot_print || false
      };

      setFormData(mappedData);

      // Check if father/mother ethnicity is "Other" to show specify fields
      if (record.father_ethnicity_id) {
        try {
          const fatherEthnicity = await api.referenceData.getEthnicityById(record.father_ethnicity_id);
          setFatherEthnicityIsOther(fatherEthnicity.is_other || false);
        } catch (error) {
          console.warn('Error checking father ethnicity:', error);
        }
      }
      
      if (record.mother_ethnicity_id) {
        try {
          const motherEthnicity = await api.referenceData.getEthnicityById(record.mother_ethnicity_id);
          setMotherEthnicityIsOther(motherEthnicity.is_other || false);
        } catch (error) {
          console.warn('Error checking mother ethnicity:', error);
        }
      }

      // Check if father has NRC data and load townships if needed
      const hasFatherNRC = !!(record.father_myanmar_nrc_area_code && record.father_myanmar_nrc_township_code && record.father_myanmar_nrc_type && record.father_myanmar_nrc_number);
      setFatherHasNRC(hasFatherNRC);
      if (hasFatherNRC) {
        try {
          const response = await apiService.axios.get(`/nrc/townships/${record.father_myanmar_nrc_area_code}`);
          setReferenceData(prev => ({
            ...prev,
            fatherNrcTownships: response.data
          }));
        } catch (error) {
          console.warn('Error loading father NRC townships:', error);
        }
      }
      setFormData(prev => ({
        ...prev,
        father_myanmar_nrc_area_code: record.father_myanmar_nrc_area_code || '',
        father_myanmar_nrc_township_code: record.father_myanmar_nrc_township_code || '',
        father_myanmar_nrc_type: record.father_myanmar_nrc_type || '',
        father_myanmar_nrc_number: record.father_myanmar_nrc_number || ''
      }));

      // Check if mother has NRC data and load townships if needed
      const hasMotherNRC = !!(record.mother_myanmar_nrc_area_code && record.mother_myanmar_nrc_township_code && record.mother_myanmar_nrc_type && record.mother_myanmar_nrc_number);
      setMotherHasNRC(hasMotherNRC);
      if (hasMotherNRC) {
        try {
          const response = await apiService.axios.get(`/nrc/townships/${record.mother_myanmar_nrc_area_code}`);
          setReferenceData(prev => ({
            ...prev,
            motherNrcTownships: response.data
          }));
        } catch (error) {
          console.warn('Error loading mother NRC townships:', error);
        }
      }
      setFormData(prev => ({
        ...prev,
        mother_myanmar_nrc_area_code: record.mother_myanmar_nrc_area_code || '',
        mother_myanmar_nrc_township_code: record.mother_myanmar_nrc_township_code || '',
        mother_myanmar_nrc_type: record.mother_myanmar_nrc_type || '',
        mother_myanmar_nrc_number: record.mother_myanmar_nrc_number || ''
      }));

      // Load related data if needed (districts -> townships -> clinics, etc.)
      if (record.district_id) {
        try {
          const townships = await api.referenceData.getTownshipsByDistrict(record.district_id);
          setReferenceData(prev => ({ ...prev, townships }));
          
          if (record.township_id) {
            const clinics = await api.referenceData.getClinicsByTownship(record.township_id);
            setReferenceData(prev => ({ ...prev, clinics }));
          }
        } catch (error) {
          console.warn('Error loading related data:', error);
        }
      }

      // Load birth place related data if needed
      if (record.baby_birth_place_district) {
        try {
          const birthPlaceTownships = await api.referenceData.getTownshipsByDistrict(record.baby_birth_place_district);
          setReferenceData(prev => ({ ...prev, birthPlaceTownships }));
          
          if (record.baby_birth_place_township) {
            const birthPlaceVillages = await api.referenceData.getVillagesByTownship(record.baby_birth_place_township);
            setReferenceData(prev => ({ ...prev, birthPlaceVillages }));
          }
        } catch (error) {
          console.warn('Error loading birth place related data:', error);
        }
      }


    } catch (error) {
      console.error('❌ Error loading existing record:', error);
      setErrors({ general: `Failed to load record data: ${error.message}` });
    } finally {
      setCheckingExisting(false);
    }
  }, [isEditMode, recordId]);

  // Handle ethnicity change to check if it's "Other"
  const handleEthnicityChange = async (type, ethnicityId) => {
    if (!ethnicityId) {
      if (type === 'father') {
        setFatherEthnicityIsOther(false);
      } else if (type === 'mother') {
        setMotherEthnicityIsOther(false);
      }
      return;
    }

    try {
      const ethnicity = await api.referenceData.getEthnicityById(ethnicityId);
      
      if (type === 'father') {
        setFatherEthnicityIsOther(ethnicity.is_other || false);
        setFormData(prev => ({
          ...prev,
          father_ethnicity_id: ethnicityId,
          father_ethnicity_other_specify: ethnicity.is_other ? prev.father_ethnicity_other_specify : ''
        }));
      } else if (type === 'mother') {
        setMotherEthnicityIsOther(ethnicity.is_other || false);
        setFormData(prev => ({
          ...prev,
          mother_ethnicity_id: ethnicityId,
          mother_ethnicity_other_specify: ethnicity.is_other ? prev.mother_ethnicity_other_specify : ''
        }));
      }
    } catch (error) {
      console.error('Error checking ethnicity:', error);
    }
  };

  // Fetch next registration ID when clinic is selected
  const fetchNextRegistrationId = async (clinicId) => {
    if (!clinicId) return;
    
    try {
      const data = await api.referenceData.getNextRegistrationId(clinicId);
      
      setFormData(prev => ({
        ...prev,
        registration_id: data.registration_id
      }));

    } catch (error) {
      console.error('Error fetching registration ID:', error);
    }
  };

  // Auto-set recorder clinic when registration clinic changes
  useEffect(() => {
    if (formData.clinic_id) {
      setFormData(prev => ({
        ...prev,
        recorder_clinic_id: formData.clinic_id
      }));
      
      // Fetch registration ID when clinic is selected
      if (!isEditMode) {
        fetchNextRegistrationId(formData.clinic_id);
      }
    }
  }, [formData.clinic_id, isEditMode]);

  useEffect(() => {
    loadReferenceData();
  }, [loadReferenceData]);

  useEffect(() => {
    if (isEditMode) {
      loadExistingRecord();
    }
  }, [isEditMode, loadExistingRecord]);

  // Debug: Log user object
  useEffect(() => {
    if (user && !user.clinic_id && !isAdmin) {
      console.warn('User does not have a clinic assigned');
    }
  }, [user, isAdmin]);

  // For non-admin users, setup location and registration ID
  useEffect(() => {
    const setupUserData = async () => {
      if (isAdmin || isEditMode || !user) return;
      
      const userClinicId = user.clinic_id; // This is the integer ID
      const userOrganizationId = user.organization_id;
      const hasDistricts = referenceData.districts.length > 0;
      const hasClinics = referenceData.clinics.length > 0;
      
      // Set organization_id if available
      if (userOrganizationId && !formData.organization_id) {
        setFormData(prev => ({ ...prev, organization_id: userOrganizationId }));
      }
      
      if (!userClinicId) {
        setErrors(prev => ({ ...prev, general: 'No clinic assigned to your account. Please contact administrator.' }));
        return;
      }
      
      try {
        // Wait for clinics to load if needed
        if (!hasClinics) return;
        
        // Find the user's clinic from all loaded clinics
        const userClinic = referenceData.clinics.find(c => c.id === userClinicId);
        
        if (!userClinic) return;
        
        const clinicCode = userClinic.clinic_id; // This is the string code like "BMA-049"
        
        // Set clinic_id in form
        if (!formData.clinic_id) {
          setFormData(prev => ({
            ...prev,
            clinic_id: clinicCode,
            recorder_clinic_id: clinicCode
          }));
        }
        
        // Fetch registration ID
        if (!formData.registration_id && clinicCode) {
          fetchNextRegistrationId(clinicCode);
        }
        
        // Setup location if not already set
        if (!formData.district_id && hasDistricts && userClinic.township_id) {
          
          // Search districts for the township
          let foundData = null;
          for (const district of referenceData.districts) {
            const townships = await api.referenceData.getTownshipsByDistrict(district.district_id);
            const township = townships?.find(t => t.township_id === userClinic.township_id);
            
            if (township) {
              foundData = {
                district_id: district.district_id,
                township_id: township.township_id,
                townships: townships || []
              };
              break;
            }
          }
          
          if (foundData) {
            // Update reference data and form data together
            setReferenceData(prev => ({ ...prev, townships: foundData.townships }));
            setFormData(prev => ({
              ...prev,
              district_id: foundData.district_id,
              township_id: foundData.township_id
            }));
          }
        }
      } catch (error) {
        console.error('Error setting up user data:', error);
      }
    };
    
    setupUserData();
  }, [isAdmin, isEditMode, user?.clinic_id, referenceData.districts.length, referenceData.clinics.length, formData.district_id, formData.registration_id, formData.clinic_id]);

  // Helper function to get day of week from date
  const getDayOfWeek = (dateString) => {
    if (!dateString) return '';
    
    // Parse date manually to avoid timezone issues
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number);
      // Create date using UTC to avoid timezone shift
      const date = new Date(Date.UTC(year, month - 1, day));
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return days[date.getUTCDay()];
    }
    
    // Fallback for other formats
    const date = new Date(dateString);
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[date.getDay()];
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Auto-calculate day of week when birth date changes
    if (name === 'baby_date_of_birth') {
      const dayOfWeek = getDayOfWeek(value);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        baby_week_of_day: dayOfWeek
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }

    // Clear specific field error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Handle NRC Area change
  const handleNrcAreaChange = async (parent, areaCode) => {
    const prefix = parent === 'father' ? 'father_myanmar_nrc' : 'mother_myanmar_nrc';
    const townshipKey = parent === 'father' ? 'fatherNrcTownships' : 'motherNrcTownships';
    
    setFormData(prev => ({
      ...prev,
      [`${prefix}_area_code`]: areaCode,
      [`${prefix}_township_code`]: '',
      [`${prefix}_type`]: '',
      [`${prefix}_number`]: ''
    }));

    if (areaCode) {
      try {
        const response = await apiService.axios.get(`/nrc/townships/${areaCode}`);
        setReferenceData(prev => ({
          ...prev,
          [townshipKey]: response.data
        }));
      } catch (error) {
        console.error(`Error loading ${parent} NRC townships:`, error);
      }
    } else {
      setReferenceData(prev => ({
        ...prev,
        [townshipKey]: []
      }));
    }
  };

  // Handle district change
  const handleDistrictChange = async (districtId) => {
    setFormData(prev => ({
      ...prev,
      district_id: districtId,
      township_id: '',
      clinic_id: ''
    }));

    if (districtId) {
      try {
        const townships = await api.referenceData.getTownshipsByDistrict(districtId);
        setReferenceData(prev => ({
          ...prev,
          townships,
          clinics: []
        }));
      } catch (error) {
        console.error('Error loading townships:', error);
      }
    } else {
      setReferenceData(prev => ({
        ...prev,
        townships: [],
        clinics: []
      }));
    }
  };

  // Handle township change
  const handleTownshipChange = async (townshipId) => {
    setFormData(prev => ({
      ...prev,
      township_id: townshipId,
      clinic_id: ''
    }));

    if (townshipId) {
      try {
        const clinics = await api.referenceData.getClinicsByTownship(townshipId);
        setReferenceData(prev => ({
          ...prev,
          clinics
        }));
      } catch (error) {
        console.error('Error loading clinics:', error);
      }
    } else {
      setReferenceData(prev => ({
        ...prev,
        clinics: []
      }));
    }
  };

  // Handle clinic change
  const handleClinicChange = async (clinicId) => {
    // Find the selected clinic to get its name
    const selectedClinic = referenceData.clinics.find(c => c.clinic_id === clinicId);
    
    setFormData(prev => ({
      ...prev,
      clinic_id: clinicId,
      recorder_clinic_name: selectedClinic ? selectedClinic.clinic_name : ''
    }));

    if (clinicId) {
      try {
        const villages = await api.referenceData.getVillagesByClinic(clinicId);
        setReferenceData(prev => ({
          ...prev,
          villages
        }));
      } catch (error) {
        console.error('Error loading villages:', error);
      }
    } else {
      setReferenceData(prev => ({
        ...prev,
        villages: []
      }));
    }
  };

  // Handle birth place district change
  const handleBirthPlaceDistrictChange = async (districtId, name) => {
    // Check if the selected district is "Other"
    const selectedDistrict = referenceData.birthPlaceDistricts.find(d => d.district_id == districtId);
    const isOtherDistrict = selectedDistrict?.district_name?.toLowerCase() === 'other';
    
    setFormData(prev => ({
      ...prev,
      [name]: districtId,
      baby_birth_place_township: '',
      baby_birth_place_village: '',
      // Clear "other" fields if not selecting "Other" district
      baby_birth_place_other_district: isOtherDistrict ? prev.baby_birth_place_other_district : '',
      baby_birth_place_other_township: isOtherDistrict ? prev.baby_birth_place_other_township : '',
      baby_birth_place_other_village: isOtherDistrict ? prev.baby_birth_place_other_village : ''
    }));

    if (districtId && !isOtherDistrict) {
      try {
        const townships = await api.referenceData.getTownshipsByDistrict(districtId);
        setReferenceData(prev => ({
          ...prev,
          birthPlaceTownships: townships || [],
          birthPlaceVillages: [] // Clear villages when district changes
        }));
      } catch (error) {
        console.error('Error loading birth place townships:', error);
      }
    } else {
      setReferenceData(prev => ({
        ...prev,
        birthPlaceTownships: [],
        birthPlaceVillages: []
      }));
    }
  };

  // Handle birth place township change
  const handleBirthPlaceTownshipChange = async (townshipId, name) => {
    setFormData(prev => ({
      ...prev,
      [name]: townshipId,
      baby_birth_place_village: ''
     }));

    if (townshipId) {
      try {
        const villages = await api.referenceData.getVillagesByTownship(townshipId);
        setReferenceData(prev => ({
          ...prev,
          birthPlaceVillages: villages || []
        }));
      } catch (error) {
        console.error('Error loading birth place villages:', error);
      }
    } else {
      setReferenceData(prev => ({
        ...prev,
        birthPlaceVillages: []
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitStatus(null);
    setErrors({});

    try {
      // Format data for API
      const formattedData = api.formHelpers.formatBirthRecordData(formData);
      
      // Validate data
      const validation = api.formHelpers.validateBirthRecord(formattedData);
      if (!validation.isValid) {
        setErrors(validation.errors);
        setLoading(false);
        return;
      }

      // Submit to API - create or update based on mode
      let result;
      if (isEditMode) {
        result = await api.birthRecords.updateBirthRecord(recordId, formattedData);
        setSubmitStatus({
          type: 'success',
          message: 'Birth record updated successfully! Redirecting to search page...',
          recordId: recordId
        });
        
        // Redirect to search page after 1.5 seconds
        setTimeout(() => {
          navigate('/search');
        }, 1500);
      } else {
        result = await api.birthRecords.createBirthRecord(formattedData);
        
        if (shouldRedirectAfterSubmit) {
          setSubmitStatus({
            type: 'success',
            message: 'Birth record created successfully! Redirecting to search page...',
            recordId: result.birthRecordId
          });
          
          // Redirect to search page after 1.5 seconds
          setTimeout(() => {
            navigate('/search');
          }, 1500);
        } else {
          setSubmitStatus({
            type: 'success',
            message: 'Birth record added successfully!',
            recordId: result.birthRecordId
          });
          
          // Scroll to top to show success message
          window.scrollTo({ top: 0, behavior: 'smooth' });
          
          // Auto-hide success message after 10 seconds
          setTimeout(() => {
            setSubmitStatus(null);
          }, 10000);
        }
      }

      // Reset form after successful submission (only for create mode)
      if (!isEditMode) {
        setFormData({
          registration_date: '',
          registration_id: '',
          organization_id: '',
          district_id: '',
          township_id: '',
          clinic_id: '',
          baby_name: '',
          baby_gender_id: '',
          baby_date_of_birth: '',
          baby_week_of_day: '',
          baby_birth_time_hour: '',
          baby_birth_time_minute: '',
          baby_birth_time_ampm: 'AM',
          baby_birth_place_choose: '',
          baby_birth_place_district: '',
          baby_birth_place_township: '',
          baby_birth_place_village: '',
          baby_birth_type: 'single',
          baby_twin_order: '',
          baby_birth_order: '',
          baby_birth_place_other_district: '',
          baby_birth_place_other_township: '',
          baby_birth_place_other_village: '',
          father_name: '',
          father_national_id: '',
          father_myanmar_nrc_area_code: '',
          father_myanmar_nrc_township_code: '',
          father_myanmar_nrc_type: '',
          father_myanmar_nrc_number: '',
          father_ethnicity_id: '',
          father_ethnicity_other_specify: '',
          father_religion_id: '',
          father_address: '',
          mother_name: '',
          mother_national_id: '',
          mother_myanmar_nrc_area_code: '',
          mother_myanmar_nrc_township_code: '',
          mother_myanmar_nrc_type: '',
          mother_myanmar_nrc_number: '',
          mother_ethnicity_id: '',
          mother_ethnicity_other_specify: '',
          mother_religion_id: '',
          mother_address: '',
          delivered_by_name: '',
          delivered_by_position_id: '',
          delivered_by_address: '',
          delivered_by_signature: false,
          recorder_name: '',
          recorder_position: '',
          recorder_clinic_id: '',
          recorder_clinic_name: '',
          recorder_signature: false,
          delivery_type_id: '',
          birth_record_baby_weight: '',
          birth_record_baby_height: '',
          birth_record_baby_head_circumference: '',
          birth_record_baby_birth_defected: false,
          birth_defect_description: '',
          mother_right_thumb_print: false,
          father_right_thumb_print: false,
          baby_right_foot_print: false
        });
        // Clear any existing errors
        setErrors({});
        // Reset NRC and ethnicity flags
        setFatherHasNRC(false);
        setMotherHasNRC(false);
        setFatherEthnicityIsOther(false);
        setMotherEthnicityIsOther(false);
      }

    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'submitting'} form:`, error);
      setSubmitStatus({
        type: 'error',
        message: error.message || `Failed to ${isEditMode ? 'update' : 'create'} birth record`
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white shadow-lg rounded-lg">
      {/* Loading state */}
      {checkingExisting && (
        <div className="flex flex-col justify-center items-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mb-4"></div>
          <span className="text-gray-600 text-lg">Loading form data...</span>
          <p className="text-sm text-gray-500 mt-2">
            If this takes too long, please check if the backend server is running on port 8000
          </p>
        </div>
      )}

      {/* Error state */}
      {errors.general && (
        <div className="p-4 rounded-md mb-6 bg-red-50 border border-red-200 text-red-800">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium">Error Loading Data</h4>
              <p className="mt-1">{errors.general}</p>
            </div>
            <button
              onClick={loadReferenceData}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              Retry
            </button>
          </div>
          <div className="mt-3 text-sm">
            <p>Troubleshooting:</p>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>Make sure the backend server is running on <code>http://localhost:8000</code></li>
              <li>Check your browser's developer console for network errors</li>
              <li>Verify CORS settings if you see access-control errors</li>
            </ul>
          </div>
        </div>
      )}

      {/* Success/Error status */}
      {submitStatus && (
        <div className={`p-6 rounded-lg mb-6 shadow-md ${
          submitStatus.type === 'success' 
            ? 'bg-green-50 border-2 border-green-500 text-green-900' 
            : 'bg-red-50 border-2 border-red-500 text-red-900'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold">{submitStatus.message}</p>
              {submitStatus.recordId && (
                <p className="mt-2 text-base font-bold">Record ID: {submitStatus.recordId}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setSubmitStatus(null)}
              className="text-gray-600 hover:text-gray-800 font-bold text-xl"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {!checkingExisting && !errors.general && (
        <>
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-800">
                {isEditMode ? 'Edit Birth Record' : 'Birth Record Registration'}
              </h2>
              {/* <span className="text-sm text-gray-500 bg-blue-100 px-3 py-1 rounded-full">
                Single Page Form
              </span> */}
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div className="bg-blue-600 h-2 rounded-full w-full"></div>
            </div>
          </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Section 1: Registration Information */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <h3 className="text-xl font-semibold text-gray-700 border-b border-gray-200 pb-3 mb-6 bg-blue-50 -m-6 p-6 rounded-t-lg">
            1. Registration Information <span className="text-red-500">*</span>
          </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Registration Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="registration_date"
                  value={formData.registration_date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Organization - Show dropdown for admin, read-only text for users */}
              {isAdmin ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="organization_id"
                    value={formData.organization_id}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select Organization</option>
                    {referenceData.organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700">
                    {referenceData.organizations.find(o => o.id === parseInt(formData.organization_id))?.name || user?.organization_name || 'Not assigned'}
                  </div>
                </div>
              )}

              {/* District - Show dropdown for admin, read-only text for users */}
              {isAdmin ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    District <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="district_id"
                    value={formData.district_id}
                    onChange={(e) => handleDistrictChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select District</option>
                    {referenceData.districts.map(district => (
                      <option key={district.district_id} value={district.district_id}>{district.district_name}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    District
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700">
                    {referenceData.districts.find(d => d.district_id === formData.district_id)?.district_name || user?.district_name || 'Not assigned'}
                  </div>
                </div>
              )}

              {/* Township - Show dropdown for admin, read-only text for users */}
              {isAdmin ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Township <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="township_id"
                    value={formData.township_id}
                    onChange={(e) => handleTownshipChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select Township</option>
                    {referenceData.townships.map(township => (
                      <option key={township.township_id} value={township.township_id}>{township.township_name}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Township
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700">
                    {referenceData.townships.find(t => t.township_id === formData.township_id)?.township_name || user?.township_name || 'Not assigned'}
                  </div>
                </div>
              )}

              {/* Clinic - Show dropdown for admin, read-only text for users */}
              {isAdmin ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Clinic <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="clinic_id"
                    value={formData.clinic_id}
                    onChange={(e) => handleClinicChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select Clinic</option>
                    {referenceData.clinics.map(clinic => (
                      <option key={clinic.clinic_id} value={clinic.clinic_id}>{clinic.clinic_name}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Clinic
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700">
                    {referenceData.clinics.find(c => c.clinic_id === formData.clinic_id)?.clinic_name || user?.clinic_name || 'Not assigned'}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Registration ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="registration_id"
                  value={formData.registration_id}
                  placeholder={
                    formData.clinic_id 
                      ? `Generating... (BR${formData.clinic_id.split('-')[1] || '___'}#####)`
                      : "Select clinic first (BR058#####)"
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-not-allowed"
                  required
                  readOnly
                  disabled
                />
                <p className="text-xs text-gray-500 mt-1">Auto-generated: BR + clinic code (3 digits) + sequence (5 digits)</p>
              </div>
            </div>
        </div>

        {/* Section 2: Baby Information */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <h3 className="text-xl font-semibold text-gray-700 border-b border-gray-200 pb-3 mb-6 bg-blue-50 -m-6 p-6 rounded-t-lg">
            2. Baby Information
          </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Baby Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Baby Name *
                </label>
                <input
                  type="text"
                  name="baby_name"
                  value={formData.baby_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Gender */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gender *
                </label>
                <select
                  name="baby_gender_id"
                  value={formData.baby_gender_id}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Gender</option>
                  {referenceData.genders.map(gender => (
                    <option key={gender.id} value={gender.id}>{gender.name}</option>
                  ))}
                </select>
              </div>

              {/* Date of Birth - Combined field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date of Birth *
                </label>
                <input
                  type="date"
                  name="baby_date_of_birth"
                  value={formData.baby_date_of_birth}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Week of Day */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Day of Week <span className="text-xs text-gray-500">(Auto-calculated)</span>
                </label>
                <input
                  type="text"
                  name="baby_week_of_day"
                  value={formData.baby_week_of_day}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 cursor-not-allowed"
                  placeholder="Select birth date first..."
                />
              </div>

              {/* Birth Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Birth Time
                </label>
                <div className="flex space-x-2">
                  <select
                    name="baby_birth_time_hour"
                    value={formData.baby_birth_time_hour}
                    onChange={handleInputChange}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Hour</option>
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                  </select>
                  <select
                    name="baby_birth_time_minute"
                    value={formData.baby_birth_time_minute}
                    onChange={handleInputChange}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Min</option>
                    {Array.from({ length: 60 }, (_, i) => (
                      <option key={i} value={i}>{i.toString().padStart(2, '0')}</option>
                    ))}
                  </select>
                  <select
                    name="baby_birth_time_ampm"
                    value={formData.baby_birth_time_ampm}
                    onChange={handleInputChange}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>

              {/* Birth Place Choose */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Birth Place Type
                </label>
                <select
                  name="baby_birth_place_choose"
                  value={formData.baby_birth_place_choose}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Birth Place</option>
                  {referenceData.birthPlaces.map(place => (
                    <option key={place.id} value={place.id}>{place.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Birth Place Location Information - Grouped Section */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
                Birth Place Location Details
              </h4>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Listed Locations (From Dropdown) - Hide when "Other" district is selected */}
                {!(formData.baby_birth_place_district && 
                   referenceData.birthPlaceDistricts.find(d => d.district_id == formData.baby_birth_place_district)?.district_name?.toLowerCase() === 'other') && (
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    {/* <h5 className="text-sm font-semibold text-green-700 mb-3 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      Select from Available Options
                    </h5> */}
                    
                    <div className="space-y-4">
                      {/* Birth Place District */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          District
                        </label>
                        <select
                          name="baby_birth_place_district"
                          value={formData.baby_birth_place_district}
                          onChange={(e) => handleBirthPlaceDistrictChange(e.target.value, e.target.name)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select District</option>
                          {referenceData.birthPlaceDistricts.map(district => (
                            <option key={district.district_id} value={district.district_id}>{district.district_desc}</option>
                          ))}
                        </select>
                      </div>

                      {/* Birth Place Township */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Township
                        </label>
                        <select
                          name="baby_birth_place_township"
                          value={formData.baby_birth_place_township}
                          onChange={(e) => handleBirthPlaceTownshipChange(e.target.value, e.target.name)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          disabled={!formData.baby_birth_place_district}
                        >
                          <option value="">Select Township</option>
                          {referenceData.birthPlaceTownships.map(township => (
                            <option key={township.township_id} value={township.township_id}>{township.township_name}</option>
                          ))}
                        </select>
                      </div>

                      {/* Birth Place Village */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Village
                        </label>
                        <select
                          name="baby_birth_place_village"
                          value={formData.baby_birth_place_village}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          disabled={!formData.baby_birth_place_township}
                        >
                          <option value="">Select Village</option>
                          {referenceData.birthPlaceVillages.map(village => (
                            <option key={village.village_id} value={village.village_id}>{village.village_name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Unlisted Locations (Manual Entry) - Show when "Other" is selected in district OR when no district is available */}
                {(
                  // Show if "Other" district is selected
                  (formData.baby_birth_place_district && 
                   referenceData.birthPlaceDistricts.find(d => d.district_id == formData.baby_birth_place_district)?.district_name?.toLowerCase() === 'other') ||
                  // Show if no districts are available (fallback option)
                  (referenceData.birthPlaceDistricts.length === 0)
                ) && (
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    
                    <div className="space-y-4">
                      {/* Show district selector if not already visible */}
                      {referenceData.birthPlaceDistricts.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            District
                          </label>
                          <select
                            name="baby_birth_place_district"
                            value={formData.baby_birth_place_district}
                            onChange={(e) => handleBirthPlaceDistrictChange(e.target.value, e.target.name)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="">Select District</option>
                            {referenceData.birthPlaceDistricts.map(district => (
                              <option key={district.district_id} value={district.district_id}>{district.district_desc}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      
                      {/* Other District Field */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Other District
                        </label>
                        <input
                          type="text"
                          name="baby_birth_place_other_district"
                          value={formData.baby_birth_place_other_district}
                          onChange={handleInputChange}
                          placeholder="Enter district name"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>

                      {/* Other Township Field */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Other Township
                        </label>
                        <input
                          type="text"
                          name="baby_birth_place_other_township"
                          value={formData.baby_birth_place_other_township}
                          onChange={handleInputChange}
                          placeholder="Enter township name"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>

                      {/* Other Village Field */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Other Village
                        </label>
                        <input
                          type="text"
                          name="baby_birth_place_other_village"
                          value={formData.baby_birth_place_other_village}
                          onChange={handleInputChange}
                          placeholder="Enter village name"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              {/* Birth Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Birth Type
                </label>
                <select
                  name="baby_birth_type"
                  value={formData.baby_birth_type}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="single">Single</option>
                  <option value="twin">Twin</option>
                  <option value="triplet">Triplet</option>
                  <option value="quadruplet">Quadruplet</option>
                </select>
              </div>

              {/* Twin Order - only show if not single */}
              {formData.baby_birth_type !== 'single' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Twin Order
                  </label>
                  <div className="flex space-x-4">
                    {[1, 2, 3, 4].map((num) => (
                      <label key={num} className="flex items-center">
                        <input
                          type="radio"
                          name="baby_twin_order"
                          value={num}
                          checked={parseInt(formData.baby_twin_order) === num}
                          onChange={handleInputChange}
                          className="mr-2 text-blue-600"
                        />
                        {num}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Birth Order */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Birth Order
                </label>
                <input
                  type="number"
                  name="baby_birth_order"
                  value={formData.baby_birth_order}
                  onChange={handleInputChange}
                  placeholder="Order of birth (1st, 2nd, etc.)"
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
        </div>

        {/* Section 3: Father Information */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <h3 className="text-xl font-semibold text-gray-700 border-b border-gray-200 pb-3 mb-6 bg-blue-50 -m-6 p-6 rounded-t-lg">
            3. Father Information <span className="text-red-500">*</span>
          </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Father Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="father_name"
                  value={formData.father_name}
                  onChange={handleInputChange}
                  placeholder="Father's full name"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Ask if father has Myanmar NRC */}
              <div className="md:col-span-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={fatherHasNRC}
                    onChange={(e) => {
                      setFatherHasNRC(e.target.checked);
                      if (!e.target.checked) {
                        // Clear NRC fields if unchecked
                        setFormData(prev => ({
                          ...prev,
                          father_myanmar_nrc_area_code: '',
                          father_myanmar_nrc_township_code: '',
                          father_myanmar_nrc_type: '',
                          father_myanmar_nrc_number: ''
                        }));
                        setReferenceData(prev => ({
                          ...prev,
                          fatherNrcTownships: []
                        }));
                      }
                    }}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Father has Myanmar NRC
                  </span>
                </label>
              </div>

              {/* Myanmar NRC Component Fields - Only show if checked */}
              {fatherHasNRC && (
                <div className="md:col-span-2">
                  <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Father Myanmar NRC Components
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      {/* NRC Area */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Area <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="father_myanmar_nrc_area_code"
                          value={formData.father_myanmar_nrc_area_code}
                          onChange={(e) => handleNrcAreaChange('father', e.target.value)}
                          required={fatherHasNRC}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select Area</option>
                          {referenceData.nrcAreas.map((area) => (
                            <option key={area.area_code} value={area.area_code}>
                              {area.area_code} - {area.area_name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* NRC Township */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Township <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="father_myanmar_nrc_township_code"
                          value={formData.father_myanmar_nrc_township_code}
                          onChange={handleInputChange}
                          disabled={!formData.father_myanmar_nrc_area_code}
                          required={fatherHasNRC}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                        >
                          <option value="">Select Township</option>
                          {referenceData.fatherNrcTownships.map((township) => (
                            <option key={township.township_code} value={township.township_code}>
                              {township.township_code}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* NRC Type */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Type <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="father_myanmar_nrc_type"
                          value={formData.father_myanmar_nrc_type}
                          onChange={handleInputChange}
                          required={fatherHasNRC}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select Type</option>
                          <option value="N">N - နိုင် (National)</option>
                          <option value="E">E - ဧည့် (Associate)</option>
                          <option value="P">P - ပြု (Naturalized)</option>
                          <option value="T">T - သာ (Temporary)</option>
                        </select>
                      </div>

                      {/* NRC Number */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="father_myanmar_nrc_number"
                          value={formData.father_myanmar_nrc_number}
                          onChange={handleInputChange}
                          placeholder="6 digits"
                          maxLength="6"
                          required={fatherHasNRC}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Format: Area/Township(Type)Number (e.g., 12/ဒဂမ(N)123456)
                    </p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Father Ethnicity <span className="text-red-500">*</span>
                </label>
                <select
                  name="father_ethnicity_id"
                  value={formData.father_ethnicity_id}
                  onChange={(e) => handleEthnicityChange('father', e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select ethnicity</option>
                  {referenceData.ethnicities.map((ethnicity) => (
                    <option key={ethnicity.id} value={ethnicity.id}>
                      {ethnicity.name}
                    </option>
                  ))}
                </select>
              </div>

              {fatherEthnicityIsOther && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Please Specify Ethnicity <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="father_ethnicity_other_specify"
                    value={formData.father_ethnicity_other_specify}
                    onChange={handleInputChange}
                    placeholder="Enter ethnicity"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Father Religion <span className="text-red-500">*</span>
                </label>
                <select
                  name="father_religion_id"
                  value={formData.father_religion_id}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select religion</option>
                  {referenceData.religions.map((religion) => (
                    <option key={religion.id} value={religion.id}>
                      {religion.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Father Address <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="father_address"
                  value={formData.father_address}
                  onChange={handleInputChange}
                  placeholder="Father's address"
                  rows="3"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
        </div>

        {/* Section 4: Mother Information (renumbered from 3 to 4) */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <h3 className="text-xl font-semibold text-gray-700 border-b border-gray-200 pb-3 mb-6 bg-yellow-50 -m-6 p-6 rounded-t-lg">
            4. Mother Information <span className="text-red-500">*</span>
          </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mother Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="mother_name"
                  value={formData.mother_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Ask if mother has Myanmar NRC */}
              <div className="md:col-span-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={motherHasNRC}
                    onChange={(e) => {
                      setMotherHasNRC(e.target.checked);
                      if (!e.target.checked) {
                        // Clear NRC fields if unchecked
                        setFormData(prev => ({
                          ...prev,
                          mother_myanmar_nrc_area_code: '',
                          mother_myanmar_nrc_township_code: '',
                          mother_myanmar_nrc_type: '',
                          mother_myanmar_nrc_number: ''
                        }));
                        setReferenceData(prev => ({
                          ...prev,
                          motherNrcTownships: []
                        }));
                      }
                    }}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Mother has Myanmar NRC
                  </span>
                </label>
              </div>

              {/* Myanmar NRC Component Fields - Only show if checked */}
              {motherHasNRC && (
                <div className="md:col-span-2">
                  <div className="bg-pink-50 p-4 rounded-md border border-pink-200">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Mother Myanmar NRC Components
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      {/* NRC Area */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Area <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="mother_myanmar_nrc_area_code"
                          value={formData.mother_myanmar_nrc_area_code}
                          onChange={(e) => handleNrcAreaChange('mother', e.target.value)}
                          required={motherHasNRC}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select Area</option>
                          {referenceData.nrcAreas.map((area) => (
                            <option key={area.area_code} value={area.area_code}>
                              {area.area_code} - {area.area_name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* NRC Township */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Township <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="mother_myanmar_nrc_township_code"
                          value={formData.mother_myanmar_nrc_township_code}
                          onChange={handleInputChange}
                          disabled={!formData.mother_myanmar_nrc_area_code}
                          required={motherHasNRC}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                        >
                          <option value="">Select Township</option>
                          {referenceData.motherNrcTownships.map((township) => (
                            <option key={township.township_code} value={township.township_code}>
                              {township.township_code}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* NRC Type */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Type <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="mother_myanmar_nrc_type"
                          value={formData.mother_myanmar_nrc_type}
                          onChange={handleInputChange}
                          required={motherHasNRC}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select Type</option>
                          <option value="N">N - နိုင် (National)</option>
                          <option value="E">E - ဧည့် (Associate)</option>
                          <option value="P">P - ပြု (Naturalized)</option>
                          <option value="T">T - သာ (Temporary)</option>
                        </select>
                      </div>

                      {/* NRC Number */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="mother_myanmar_nrc_number"
                          value={formData.mother_myanmar_nrc_number}
                          onChange={handleInputChange}
                          placeholder="6 digits"
                          maxLength="6"
                          required={motherHasNRC}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Format: Area/Township(Type)Number (e.g., 12/ဒဂမ(N)123456)
                    </p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ethnicity <span className="text-red-500">*</span>
                </label>
                <select
                  name="mother_ethnicity_id"
                  value={formData.mother_ethnicity_id}
                  onChange={(e) => handleEthnicityChange('mother', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Ethnicity</option>
                  {referenceData.ethnicities.map(ethnicity => (
                    <option key={ethnicity.id} value={ethnicity.id}>{ethnicity.name}</option>
                  ))}
                </select>
              </div>

              {motherEthnicityIsOther && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Please Specify Ethnicity <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="mother_ethnicity_other_specify"
                    value={formData.mother_ethnicity_other_specify}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Religion <span className="text-red-500">*</span>
                </label>
                <select
                  name="mother_religion_id"
                  value={formData.mother_religion_id}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Religion</option>
                  {referenceData.religions.map(religion => (
                    <option key={religion.id} value={religion.id}>{religion.name}</option>
                  ))}
                </select>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="mother_address"
                  value={formData.mother_address}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                  required
                />
              </div>
            </div>
        </div>

        {/* Section 5: Birth Record Information */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <h3 className="text-xl font-semibold text-gray-700 border-b border-gray-200 pb-3 mb-6 bg-purple-50 -m-6 p-6 rounded-t-lg">
            5. Birth Record Information
          </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Delivery Type
                </label>
                <select
                  name="delivery_type_id"
                  value={formData.delivery_type_id}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Delivery Type</option>
                  {referenceData.deliveryTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>

              {/* Birth Record Baby Measurements */}
              <div className="col-span-2">
                <h4 className="text-md font-medium text-gray-700 mb-3">Baby Measurements (Birth Record) <span className="text-red-500">*</span></h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Weight (kg) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name="birth_record_baby_weight"
                      value={formData.birth_record_baby_weight}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Height (cm) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name="birth_record_baby_height"
                      value={formData.birth_record_baby_height}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Head Circumference (cm) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name="birth_record_baby_head_circumference"
                      value={formData.birth_record_baby_head_circumference}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div className="flex items-center">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="birth_record_baby_birth_defected"
                        checked={formData.birth_record_baby_birth_defected}
                        onChange={handleInputChange}
                        className="text-blue-600"
                      />
                      <span className="text-sm font-medium text-gray-700">Birth Defected</span>
                    </label>
                  </div>

                  {/* Birth Defect Description - conditional */}
                  {formData.birth_record_baby_birth_defected && (
                    <div className="col-span-3">
                      <label htmlFor="birth_defect_description" className="block text-sm font-medium text-gray-700 mb-1">
                        Birth Defect Description *
                      </label>
                      <textarea
                        id="birth_defect_description"
                        name="birth_defect_description"
                        value={formData.birth_defect_description}
                        onChange={handleInputChange}
                        rows={3}
                        required={formData.birth_record_baby_birth_defected}
                        placeholder="Please describe the birth defect in detail..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Prints Section */}
              <div className="col-span-2">
                <h4 className="text-md font-medium text-gray-700 mb-3">Prints Taken</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="mother_right_thumb_print"
                        checked={formData.mother_right_thumb_print}
                        onChange={handleInputChange}
                        className="text-blue-600"
                      />
                      <span className="text-sm font-medium text-gray-700">Mother Right Thumb Print</span>
                    </label>
                  </div>

                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="father_right_thumb_print"
                        checked={formData.father_right_thumb_print}
                        onChange={handleInputChange}
                        className="text-blue-600"
                      />
                      <span className="text-sm font-medium text-gray-700">Father Right Thumb Print</span>
                    </label>
                  </div>

                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="baby_right_foot_print"
                        checked={formData.baby_right_foot_print}
                        onChange={handleInputChange}
                        className="text-blue-600"
                      />
                      <span className="text-sm font-medium text-gray-700">Baby Right Foot Print</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
        </div>

        {/* Section 6: Delivered By Information */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <h3 className="text-xl font-semibold text-gray-700 border-b border-gray-200 pb-3 mb-6 bg-orange-50 -m-6 p-6 rounded-t-lg">
            6. Delivered By <span className="text-red-500">*</span>
          </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Delivered by <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="delivered_by_name"
                  value={formData.delivered_by_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Position <span className="text-red-500">*</span>
                </label>
                <select
                  name="delivered_by_position_id"
                  value={formData.delivered_by_position_id}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Position</option>
                  {referenceData.positions.map(position => (
                    <option key={position.id} value={position.id}>{position.name}</option>
                  ))}
                </select>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address
                </label>
                <textarea
                  name="delivered_by_address"
                  value={formData.delivered_by_address}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter address of the person who delivered the baby"
                />
              </div>

              <div className="col-span-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="delivered_by_signature"
                    checked={formData.delivered_by_signature}
                    onChange={handleInputChange}
                    className="text-blue-600"
                  />
                  <span className="text-sm font-medium text-gray-700">Signature Provided</span>
                </label>
              </div>
            </div>
        </div>

        {/* Section 7: Certified By Information */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <h3 className="text-xl font-semibold text-gray-700 border-b border-gray-200 pb-3 mb-6 bg-indigo-50 -m-6 p-6 rounded-t-lg">
            7. Certified By <span className="text-red-500">*</span>
          </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Certified by <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="recorder_name"
                  value={formData.recorder_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Position
                </label>
                <select
                  name="recorder_position"
                  value={formData.recorder_position}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Position</option>
                  {referenceData.positions.map(position => (
                    <option key={position.id} value={position.name}>{position.name}</option>
                  ))}
                </select>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Clinic Name <span className="text-red-500">*</span>
                </label>
                <select
                  name="recorder_clinic_name"
                  value={formData.recorder_clinic_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled
                  required
                >
                  <option value="">Select Clinic</option>
                  {referenceData.clinics.map(clinic => (
                    <option key={clinic.clinic_id} value={clinic.clinic_name}>{clinic.clinic_name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Clinic is automatically set to match registration clinic</p>
              </div>

              <div className="col-span-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="recorder_signature"
                    checked={formData.recorder_signature}
                    onChange={handleInputChange}
                    className="text-blue-600"
                  />
                  <span className="text-sm font-medium text-gray-700">Signature Provided</span>
                </label>
              </div>
            </div>
        </div>

        {/* Submit Button */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex justify-center gap-4">
            {isEditMode && (
              <button
                type="button"
                onClick={() => navigate('/search')}
                className="px-8 py-3 bg-gray-500 text-white rounded-md font-medium hover:bg-gray-600 transition-colors duration-200 text-lg"
              >
                Cancel
              </button>
            )}
            {!isEditMode && (
              <button
                type="submit"
                disabled={loading}
                onClick={() => setShouldRedirectAfterSubmit(false)}
                className={`px-8 py-3 text-white rounded-md font-medium transition-colors duration-200 text-lg ${
                  loading 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {loading ? 'Processing...' : 'Submit & Add Another'}
              </button>
            )}
            <button
              type="submit"
              disabled={loading}
              onClick={() => setShouldRedirectAfterSubmit(true)}
              className={`px-8 py-3 text-white rounded-md font-medium transition-colors duration-200 text-lg ${
                loading 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {loading ? 'Processing...' : (isEditMode ? 'Update Birth Record' : 'Submit Birth Record')}
            </button>
          </div>
        </div>
      </form>
        </>
      )}
    </div>
  );
};

export default BirthRecordForm;
